const buildItemLocationLabel = (entry, formatCombatFeet) => {
  if (entry.state === 'embedded') {
    return `embedded in ${entry.embeddedIn === 'firstCombatant' ? 'first combatant' : 'second combatant'}`;
  }

  if (entry.relativeTo === 'firstCombatant') {
    return `${entry.state} by first combatant`;
  }

  if (entry.relativeTo === 'secondCombatant') {
    return `${entry.state} by second combatant`;
  }

  if (entry.position) {
    return `${entry.state} at (${formatCombatFeet(entry.position.x)}, ${formatCombatFeet(
      entry.position.y
    )}, ${formatCombatFeet(entry.position.z)})`;
  }

  return entry.state;
};

const formatCombatPanelText = (value, fallback = '') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (typeof value === 'object') {
    if (typeof value.description === 'string' && value.description.trim()) {
      return value.description;
    }

    if (typeof value.effect === 'string' && value.effect.trim()) {
      return value.effect;
    }

    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }

  return fallback;
};

const buildSpellEffectSideLabel = (side) => {
  if (side === 'firstCombatant') {
    return 'first combatant';
  }

  if (side === 'secondCombatant') {
    return 'second combatant';
  }

  return 'combat';
};

const buildSpellEffectLine = (effect) => {
  if (!effect) {
    return '';
  }

  const modifierParts = [];
  if (Number(effect?.modifiers?.initiativeBonus)) {
    modifierParts.push(`Init ${effect.modifiers.initiativeBonus >= 0 ? '+' : ''}${effect.modifiers.initiativeBonus}`);
  }
  if (Number(effect?.modifiers?.actionBonus)) {
    modifierParts.push(`Action ${effect.modifiers.actionBonus >= 0 ? '+' : ''}${effect.modifiers.actionBonus}`);
  }
  if (Number(effect?.modifiers?.defensiveBonus)) {
    modifierParts.push(`DB ${effect.modifiers.defensiveBonus >= 0 ? '+' : ''}${effect.modifiers.defensiveBonus}`);
  }
  if (Number(effect?.modifiers?.movementBonus)) {
    modifierParts.push(`Move ${effect.modifiers.movementBonus >= 0 ? '+' : ''}${effect.modifiers.movementBonus} ft`);
  }

  const durationLabel =
    effect.roundsRemaining === null ? 'combat-long' : `${effect.roundsRemaining} round${effect.roundsRemaining === 1 ? '' : 's'} left`;

  return [
    `${effect.spellName || 'Unknown spell'} by ${effect.castByName || buildSpellEffectSideLabel(effect.castBy)}`,
    `affecting ${buildSpellEffectSideLabel(effect.appliesToSide)}`,
    durationLabel,
    modifierParts.join(', '),
    formatCombatPanelText(effect.notes),
  ]
    .filter(Boolean)
    .join(' | ');
};

function CombatTestPanel({
  selectedCombatantOneId,
  selectedCombatantTwoId,
  setSelectedCombatantOneId,
  setSelectedCombatantTwoId,
  combatState,
  combatItemLocationEntries,
  combatantOptions,
  combatantOneDetail,
  combatantTwoDetail,
  combatActionResult,
  combatResponseActionResult,
  calculateCombatantDbForAction,
  buildCombatStatusLine,
  combatHistoryEntries,
  combatHistoryEndRef,
  combatAction,
  setCombatAction,
  isCombatOver,
  handleCombatActionSubmit,
  isSubmittingCombatAction,
  combatWinnerLabel,
  isGeneratingCombatNarration,
  handleCombatantRefresh,
  combatRollLogEntries,
  buildInitiativeLogLine,
  buildActionLogLine,
  buildMovementLogLine,
  buildOutcomeLogLine,
  formatCombatFeet,
}) {
  const activeSpellEffects = Array.isArray(combatState?.activeSpellEffects) ? combatState.activeSpellEffects : [];
  const expiredSpellEffects = Array.isArray(combatState?.spellEffectHistory) ? combatState.spellEffectHistory : [];

  return (
    <div className="editor-layout">
      <section className="editor-section">
        <div className="panel-header-row">
          <h3>Combatants</h3>
          <button
            type="button"
            className="secondary-button button-with-icon"
            onClick={handleCombatantRefresh}
            disabled={!selectedCombatantOneId && !selectedCombatantTwoId}
          >
            <span>Refresh Stats</span>
          </button>
        </div>
        <div className="panel-status">
          Distance: {formatCombatFeet(combatState.distanceFeet)} ft
          {' | '}
          First Position: ({formatCombatFeet(combatState.firstCombatantPosition.x)},{' '}
          {formatCombatFeet(combatState.firstCombatantPosition.y)},{' '}
          {formatCombatFeet(combatState.firstCombatantPosition.z)})
          {' | '}
          Second Position: ({formatCombatFeet(combatState.secondCombatantPosition.x)},{' '}
          {formatCombatFeet(combatState.secondCombatantPosition.y)},{' '}
          {formatCombatFeet(combatState.secondCombatantPosition.z)})
        </div>
        <details className="combat-details">
          <summary>
            Item Locations
            <span className="combat-details-meta">
              {combatItemLocationEntries.length === 0 ? 'None tracked' : `${combatItemLocationEntries.length} tracked`}
            </span>
          </summary>
          <div className="combat-details-body">
            {combatItemLocationEntries.length === 0 ? (
              <div className="panel-status combat-panel-status">None tracked yet.</div>
            ) : (
              combatItemLocationEntries.map((entry) => (
                <div key={`combat-location-${entry.idEntity}`} className="combat-log-line">
                  <strong>{entry.label}:</strong> {buildItemLocationLabel(entry, formatCombatFeet)}
                </div>
              ))
            )}
          </div>
        </details>
        <div className="editor-grid editor-grid-two">
          <div>
            <label htmlFor="combatant-one" className="editor-field">
              <span className="field-label">Your Being</span>
              <select
                id="combatant-one"
                className="select-input"
                value={selectedCombatantOneId}
                onChange={(event) => setSelectedCombatantOneId(event.target.value)}
              >
                <option value="">Select...</option>
                {combatantOptions.map((option) => (
                  <option key={`combatant-one-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {combatantOneDetail && (
              <div className="combatant-summary">
                <div className="combatant-summary-stats">
                  HP: {combatantOneDetail.currentHp ?? 0}/{combatantOneDetail.maxHp ?? 0}
                  {' | '}
                  PP: {combatantOneDetail.currentPp ?? 0}/{combatantOneDetail.maxPp ?? 0}
                  {' | '}
                  DB: {calculateCombatantDbForAction(combatantOneDetail, combatActionResult, combatState, 'firstCombatant')}
                </div>
                <div className="combatant-summary-status">
                  Status: {buildCombatStatusLine(combatantOneDetail) || 'None'}
                </div>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="combatant-two" className="editor-field">
              <span className="field-label">Opponent Being</span>
              <select
                id="combatant-two"
                className="select-input"
                value={selectedCombatantTwoId}
                onChange={(event) => setSelectedCombatantTwoId(event.target.value)}
              >
                <option value="">Select...</option>
                {combatantOptions.map((option) => (
                  <option key={`combatant-two-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {combatantTwoDetail && (
              <div className="combatant-summary">
                <div className="combatant-summary-stats">
                  HP: {combatantTwoDetail.currentHp ?? 0}/{combatantTwoDetail.maxHp ?? 0}
                  {' | '}
                  PP: {combatantTwoDetail.currentPp ?? 0}/{combatantTwoDetail.maxPp ?? 0}
                  {' | '}
                  DB: {calculateCombatantDbForAction(
                    combatantTwoDetail,
                    combatResponseActionResult,
                    combatState,
                    'secondCombatant'
                  )}
                </div>
                <div className="combatant-summary-status">
                  Status: {buildCombatStatusLine(combatantTwoDetail) || 'None'}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {selectedCombatantOneId && selectedCombatantTwoId && (
        <section className="editor-section">
          <details className="combat-details" open>
            <summary>
              Spell Effects
              <span className="combat-details-meta">
                {activeSpellEffects.length === 0
                  ? 'No active spells'
                  : `${activeSpellEffects.length} active${expiredSpellEffects.length ? ` | ${expiredSpellEffects.length} expired` : ''}`}
              </span>
            </summary>
            <div className="combat-details-body">
              {activeSpellEffects.length === 0 ? (
                <div className="panel-status combat-panel-status">No active combat spell effects.</div>
              ) : (
                activeSpellEffects.map((effect) => (
                  <div key={effect.id} className="combat-log-line">
                    {buildSpellEffectLine(effect)}
                  </div>
                ))
              )}
              {expiredSpellEffects.length > 0 && (
                <>
                  <div className="combat-history-round">Expired</div>
                  {expiredSpellEffects
                    .slice()
                    .reverse()
                    .slice(0, 5)
                    .map((effect) => (
                      <div key={`${effect.id}-expired`} className="combat-log-line">
                        {buildSpellEffectLine(effect)}
                      </div>
                    ))}
                </>
              )}
            </div>
          </details>

          <details className="combat-details" open>
            <summary>
              Combat Log
              <span className="combat-details-meta">
                {combatHistoryEntries.length === 0
                  ? 'No rounds yet'
                  : `${combatHistoryEntries.length} round${combatHistoryEntries.length === 1 ? '' : 's'}`}
              </span>
            </summary>
            <div className="combat-details-body">
              <div className="combat-history">
                {combatHistoryEntries.length === 0 ? (
                  <div className="empty-grid">No combat rounds yet.</div>
                ) : (
                  combatHistoryEntries.map((entry) => (
                    <article key={`combat-history-entry-${entry.roundNumber}`} className="combat-history-entry">
                      <div className="combat-history-round">
                        Round {entry.roundNumber}
                        {entry.startDistanceFeet !== null && entry.endDistanceFeet !== null
                          ? ` | Distance ${formatCombatFeet(entry.startDistanceFeet)} ft -> ${formatCombatFeet(
                              entry.endDistanceFeet
                            )} ft`
                          : ''}
                      </div>
                      {entry.playerActionDescription ? (
                        <p className="combat-history-paragraph">
                          <strong>Action:</strong> {formatCombatPanelText(entry.playerActionDescription)}
                        </p>
                      ) : null}
                      {entry.roundDescription ? (
                        <p className="combat-history-paragraph">{formatCombatPanelText(entry.roundDescription)}</p>
                      ) : null}
                    </article>
                  ))
                )}
                <div ref={combatHistoryEndRef} />
              </div>
            </div>
          </details>

          <details className="combat-details">
            <summary>
              Roll Log
              <span className="combat-details-meta">
                {combatRollLogEntries.length === 0
                  ? 'No rounds yet'
                  : `${combatRollLogEntries.length} entr${combatRollLogEntries.length === 1 ? 'y' : 'ies'}`}
              </span>
            </summary>
            <div className="combat-details-body">
              {combatRollLogEntries.length === 0 ? (
                <div className="panel-status combat-panel-status">No roll data yet.</div>
              ) : (
                combatRollLogEntries.map((entry) => (
                  <article key={entry.key} className="combat-history-entry combat-roll-entry">
                    <div className="combat-history-round">
                      {entry.title}
                      {entry.subtitle ? ` | ${entry.subtitle}` : ''}
                      {entry.pending ? ' | Resolving' : ''}
                    </div>
                    {entry.playerActionDescription ? (
                      <div className="combat-log-line">
                        <strong>Action:</strong> {formatCombatPanelText(entry.playerActionDescription)}
                      </div>
                    ) : null}
                    <div className="combat-log-block">
                      <div className="combat-log-combatant">
                        <div className="combat-log-title">Your Being</div>
                        <div className="combat-log-line">
                          {buildInitiativeLogLine(entry.rollResults?.firstCombatant?.initiative)}
                        </div>
                        <div className="combat-log-line">
                          {buildActionLogLine(entry.rollResults?.firstCombatant?.action)}
                        </div>
                        {entry.rollResults?.firstCombatant?.movement ? (
                          <div className="combat-log-line">
                            {buildMovementLogLine(entry.rollResults.firstCombatant.movement)}
                          </div>
                        ) : null}
                        <div className="combat-log-line">
                          {buildOutcomeLogLine(
                            entry.rollResults?.firstCombatant?.outcome,
                            entry.rollResults?.firstCombatant?.skipped,
                            entry.rollResults?.firstCombatant?.skipReason
                          )}
                        </div>
                      </div>
                      <div className="combat-log-combatant">
                        <div className="combat-log-title">Opponent</div>
                        <div className="combat-log-line">
                          {buildInitiativeLogLine(entry.rollResults?.secondCombatant?.initiative)}
                        </div>
                        <div className="combat-log-line">
                          {buildActionLogLine(entry.rollResults?.secondCombatant?.action)}
                        </div>
                        {entry.rollResults?.secondCombatant?.movement ? (
                          <div className="combat-log-line">
                            {buildMovementLogLine(entry.rollResults.secondCombatant.movement)}
                          </div>
                        ) : null}
                        <div className="combat-log-line">
                          {buildOutcomeLogLine(
                            entry.rollResults?.secondCombatant?.outcome,
                            entry.rollResults?.secondCombatant?.skipped,
                            entry.rollResults?.secondCombatant?.skipReason
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </details>

          <label htmlFor="combat-action" className="editor-field">
            <span className="field-label">Describe Your Action</span>
            <textarea
              id="combat-action"
              className="text-area editor-textarea"
              value={combatAction}
              onChange={(event) => setCombatAction(event.target.value)}
              placeholder={isCombatOver ? 'Combat is over.' : 'Describe what your being does.'}
              disabled={isCombatOver}
            />
          </label>
          <div className="button-row">
            <button
              type="button"
              className="button-with-icon"
              onClick={handleCombatActionSubmit}
              disabled={isCombatOver || isSubmittingCombatAction || !combatAction.trim()}
            >
              <span>{isSubmittingCombatAction ? 'Submitting...' : 'Submit Action'}</span>
            </button>
          </div>
          {isCombatOver && (
            <div className="panel-status">Combat over. {combatWinnerLabel ? `${combatWinnerLabel} wins.` : ''}</div>
          )}
          {isGeneratingCombatNarration && (
            <div className="panel-status">Generating combat narration...</div>
          )}
        </section>
      )}
    </div>
  );
}

export default CombatTestPanel;
